import { useEffect, useMemo, useState } from 'react';
import { cloneSchedule, listSchedulesForLocation } from '@/lib/scheduling';
import type { Schedule } from '@/lib/types';

export interface CopyMonthDialogProps {
  open: boolean;
  locationId: string;
  targetMonthStart: string;
  onClose: () => void;
  onDone: () => void | Promise<void>;
}

export function CopyMonthDialog(props: CopyMonthDialogProps) {
  const { open, locationId, targetMonthStart, onClose, onDone } = props;

  const [available, setAvailable] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [sourceId, setSourceId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ copied: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    setDone(null);
    setSourceId('');
    listSchedulesForLocation(locationId)
      .then((list) => {
        const filtered = list.filter((s) => s.month_start_date < targetMonthStart);
        setAvailable(filtered);
        if (filtered.length > 0) setSourceId(filtered[0].id);
      })
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, [open, locationId, targetMonthStart]);

  const sourceLabel = useMemo(() => {
    const s = available.find((x) => x.id === sourceId);
    return s ? formatMonthLabel(s.month_start_date) + ' · ' + s.status : '';
  }, [available, sourceId]);

  if (!open) return null;

  async function onConfirm() {
    if (!sourceId) return;
    setSubmitting(true);
    setError(null);
    try {
      await cloneSchedule(sourceId, targetMonthStart);
      setDone({ copied: 0 });
      await onDone();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-lg border border-border bg-card shadow-lg">
        <header className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-base font-medium">Copy from previous month</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close">×</button>
        </header>

        <div className="p-4 space-y-4">
          <p className="text-xs text-muted-foreground">
            Same-weekday-index mapping: 1st Monday of source → 1st Monday of {formatMonthLabel(targetMonthStart)}, etc.
          </p>

          {loading ? (
            <p className="text-sm text-muted-foreground">Loading prior schedules…</p>
          ) : available.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
              No prior schedules at this location yet. Build one first, then copy from it next month.
            </div>
          ) : (
            <div className="space-y-1">
              <label htmlFor="source_schedule" className="block text-xs font-medium">Source month</label>
              <select id="source_schedule" value={sourceId} onChange={(e) => setSourceId(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                {available.map((s) => (
                  <option key={s.id} value={s.id}>{formatMonthLabel(s.month_start_date)} · {s.status}</option>
                ))}
              </select>
              <p className="text-[11px] text-muted-foreground">Will copy from {sourceLabel} into {formatMonthLabel(targetMonthStart)}.</p>
            </div>
          )}

          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
            <p className="font-medium">Conflicts auto-flag with override.</p>
            <p>Shifts that land on a closed day, overlap an existing shift, or fall outside operating hours get saved with an &quot;auto-copy&quot; override reason. Look for the ⚠ chips after copy and review.</p>
          </div>

          {error && <p className="rounded-md border border-destructive bg-destructive/10 p-2 text-xs text-destructive">{error}</p>}

          {done && (
            <p className="rounded-md border border-emerald-300 bg-emerald-50 p-2 text-sm text-emerald-900">
              Copy complete. Review the calendar — chips with ⚠ are the ones that conflicted.
            </p>
          )}
        </div>

        <footer className="flex justify-end gap-2 p-4 border-t border-border">
          <button type="button" onClick={onClose} className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/40">Close</button>
          {!done && (
            <button type="button" onClick={onConfirm} disabled={!sourceId || submitting || available.length === 0} className="rounded-md bg-primary text-primary-foreground px-4 py-1.5 text-sm font-medium hover:opacity-90 disabled:opacity-50">
              {submitting ? 'Copying…' : 'Copy'}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

function formatMonthLabel(monthStart: string): string {
  const [y, m] = monthStart.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}
